import { Button, List, Popconfirm, Space, Tooltip, Typography, Upload, message } from 'antd';
import {
  DeleteOutlined,
  DownloadOutlined,
  FileOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import type { RcFile } from 'antd/es/upload';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OwnerType } from '@never2busy/shared';
import { filesApi, type FileAsset } from '../../api/endpoints/files';

const { Dragger } = Upload;
const { Text } = Typography;

interface AttachmentsListProps {
  ownerType: OwnerType;
  ownerId: number;
  /** Si true, oculta el dropzone y solo muestra la lista. */
  readOnly?: boolean;
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function AttachmentsList({
  ownerType,
  ownerId,
  readOnly = false,
}: AttachmentsListProps): JSX.Element {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['files', ownerType, ownerId],
    queryFn: () => filesApi.list(ownerType, ownerId),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: RcFile) => filesApi.upload(file, ownerType, ownerId),
    onSuccess: () => {
      message.success('Archivo subido');
      void queryClient.invalidateQueries({ queryKey: ['files', ownerType, ownerId] });
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'No se pudo subir el archivo';
      message.error(msg);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => filesApi.remove(id),
    onSuccess: () => {
      message.success('Archivo eliminado');
      void queryClient.invalidateQueries({ queryKey: ['files', ownerType, ownerId] });
    },
    onError: () => message.error('No se pudo eliminar el archivo'),
  });

  const uploadProps: UploadProps = {
    multiple: true,
    showUploadList: false,
    beforeUpload: (file) => {
      uploadMutation.mutate(file);
      return false; // evita el upload nativo
    },
  };

  return (
    <div>
      {!readOnly && (
        <Dragger {...uploadProps} disabled={uploadMutation.isPending} style={{ marginBottom: 16 }}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Arrastra archivos aquí o haz clic para seleccionar</p>
          <p className="ant-upload-hint">Tamaño máximo 10 MB. PDF, Office, imágenes, ZIP.</p>
        </Dragger>
      )}

      <List<FileAsset>
        loading={isLoading}
        dataSource={data ?? []}
        locale={{ emptyText: 'Sin archivos adjuntos' }}
        renderItem={(file) => (
          <List.Item
            actions={[
              <Tooltip key="dl" title="Descargar">
                <Button
                  type="text"
                  icon={<DownloadOutlined />}
                  onClick={() => {
                    void filesApi.download(file.id, file.originalName).catch(() => {
                      message.error('No se pudo descargar el archivo');
                    });
                  }}
                />
              </Tooltip>,
              !readOnly ? (
                <Popconfirm
                  key="rm"
                  title="¿Eliminar archivo?"
                  okText="Sí"
                  cancelText="No"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => removeMutation.mutate(file.id)}
                >
                  <Tooltip title="Eliminar">
                    <Button type="text" icon={<DeleteOutlined />} danger />
                  </Tooltip>
                </Popconfirm>
              ) : null,
            ].filter(Boolean)}
          >
            <List.Item.Meta
              avatar={<FileOutlined style={{ fontSize: 24, color: '#1677ff' }} />}
              title={file.originalName}
              description={
                <Space split={<Text type="secondary">·</Text>}>
                  <Text type="secondary">{humanSize(file.size)}</Text>
                  <Text type="secondary">{file.mimeType}</Text>
                  <Text type="secondary">
                    {new Date(file.createdAt).toLocaleString('es-MX')}
                  </Text>
                </Space>
              }
            />
          </List.Item>
        )}
      />
    </div>
  );
}
